<script setup lang="ts">
import { DotsCircleHorizontalIcon } from '@heroicons/vue/solid'

import { useWalletState, WalletState } from '../../../utilities/account/useWallet'
import BaseButtonMuted from '../../base/BaseButtonMuted.vue'
import { formatCurrency, formatDateTime } from '../../../utilities/helpers'
import { openConfirmDialog } from '../../../utilities/useDialog'
import { i18n } from '../../../localization'
import { MoneyWithdrawal, putCancelWalletMoneyWithdrawal } from '../../../utilities/api'
import { notify } from '../../../utilities/useNotifications'
import { AxiosError } from 'axios'
import { computed } from 'vue'

const walletState = useWalletState() as WalletState

const pendingWithdrawals = computed(() =>
    walletState.moneyWithdrawals.filter((mw: MoneyWithdrawal) => mw.status === 'pending')
)

async function confirmCancel(id: string): Promise<void> {
    const confirm = await openConfirmDialog(
        i18n.global.t('wallet.cancel_wallet_withdrawal_confirm_heading'),
        i18n.global.t('wallet.cancel_wallet_withdrawal_confirm_description'),
        {
            type: 'Warning',
        }
    )
    if (confirm) {
        cancel(id)
    }
}

function cancel(id: string): void {
    putCancelWalletMoneyWithdrawal(id)
        .then(() => {
            notify('Success', i18n.global.t('success'), i18n.global.t('wallet.cancel_wallet_withdrawal_success'))
            walletState.fetchWalletDataSilent()
        })
        .catch((error: AxiosError) => {
            notify(
                'Error',
                i18n.global.t('error'),
                error.response?.data?.message ||
                    error.response?.data?.errorMessage ||
                    i18n.global.t('wallet.cancel_wallet_withdrawal_error')
            )
        })
}
</script>

<template>
    <div class="space-y-2">
        <div
            v-for="withdrawal in pendingWithdrawals"
            :key="withdrawal.id"
            class="flex justify-between rounded-md border border-blue-500 bg-white p-4 shadow">
            <div class="space-y-2 pr-2">
                <h3 class="flex text-sm">
                    <DotsCircleHorizontalIcon class="mr-1 w-5 text-blue-400" />
                    {{ $t('wallet.withdrawal_pending_heading') }}
                    <span class="mx-1 font-semibold text-gray-400">{{ formatDateTime(withdrawal.created_at) }}</span>
                </h3>
                <p class="text-md">
                    <span class="font-s">{{ $t('wallet.withdrawal_amount') }}</span
                    >:
                    <span class="font-semibold text-gray-900">{{
                        formatCurrency(withdrawal.amount, walletState.wallet.currency)
                    }}</span>
                </p>

                <p class="text-sm">
                    {{ $t('wallet.confirm_withdrawal_description') }}
                </p>
            </div>
            <div class="flex items-center">
                <BaseButtonMuted class="whitespace-nowrap" @click="confirmCancel(withdrawal.id)">{{
                    $t('wallet.cancel_wallet_withdrawal')
                }}</BaseButtonMuted>
            </div>
        </div>
    </div>
</template>
